import "../redesign.css";
import { Link } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { domainUrl } from "app/Api/axios";
import { useLocation } from "react-router-dom";

/* ── Pricing calculator math (exact spec) ── */
const calcPrice = (seats, addon, annual) => {
  const s1 = Math.min(seats, 50) * 50;
  const s2 = Math.max(0, Math.min(seats - 50, 50)) * 40;
  const s3 = Math.max(0, seats - 100) * 30;
  const base = s1 + s2 + s3;
  const afterFloor = Math.max(base, 1500);
  const addonCost = addon ? seats * 100 : 0;
  const monthly = afterFloor + addonCost;
  const perMonth = annual ? Math.round(monthly * 0.83) : monthly;
  const discount = annual ? monthly - perMonth : 0;
  return { base, afterFloor, addonCost, monthly, perMonth, discount, floorApplied: base < 1500 };
};

const fmtINR = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n);

/* ── FAQ data ── */
const faqItems = [
  { q: 'Is there a free trial?', a: 'Yes — every new company gets a free trial with full access to all features. No credit card required.' },
  { q: 'What does the ₹1,500 minimum mean?', a: 'The minimum monthly charge is ₹1,500 regardless of team size. This covers up to 30 users at the ₹50/user rate. Teams larger than 30 will pay based on the graduated slab rates.' },
  { q: 'How does the graduated pricing work?', a: 'Your first 50 users are ₹50/user/month, seats 51–100 are ₹40/user/month, and every seat above 100 is ₹30/user/month. The slabs stack, so a 75-person team pays (50 × ₹50) + (25 × ₹40) = ₹3,500/month.' },
  { q: 'Is GST included in the listed prices?', a: 'No — all prices shown are exclusive of 18% GST. GST will be added to your invoice.' },
  { q: 'What is the Activity Tracking add-on?', a: 'For ₹100 per license per month, the desktop agent captures active apps, idle time and productivity breakdowns. It\u2019s optional \u2014 the base plan works without it.' },
  { q: 'Can I switch between monthly and annual billing?', a: 'Yes — you can switch at any time. Annual billing saves approximately 17% compared to monthly billing. The annual amount is billed upfront.' },
];

/* ── Comparison table data ── */
const compRows = [
  { feat: 'GPS & mobile attendance', s: true, m: true, e: true },
  { feat: 'Leave management', s: true, m: true, e: true },
  { feat: 'Self-service portal', s: true, m: true, e: true },
  { feat: 'Guided onboarding', s: true, m: true, e: true },
  { feat: 'Geo-fencing', s: false, m: true, e: true },
  { feat: 'Biometric & facial recognition', s: false, m: true, e: true },
  { feat: 'Productivity analytics', s: false, m: true, e: true },
  { feat: 'Payroll integration', s: false, m: true, e: true },
  { feat: 'Priority support', s: false, m: true, e: true },
  { feat: 'SSO & advanced security', s: false, m: false, e: true },
  { feat: 'Multi-location management', s: false, m: false, e: true },
  { feat: 'Dedicated account manager & SLA', s: false, m: false, e: true },
];

const includedItems = [
  'Real-time attendance monitoring', 'GPS-based & geo-fencing', 'Biometric & facial recognition',
  'Desktop/web/mobile apps', 'Leave management & approvals', 'Shift & schedule management',
  'Employee self-service', 'Productivity analytics & reports', 'Payroll integration',
  'Multi-location & hybrid/remote support',
];

/* ════════ MAIN COMPONENT ════════ */
const PricingRedesign = () => {
  const [billing, setBilling] = useState('monthly');
  const [seats, setSeats] = useState(30);
  const [addon, setAddon] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const location = useLocation();
  const canonicalURL = `${domainUrl}${location.pathname}`;
  const annual = billing === 'annual';
  const price = useMemo(() => calcPrice(seats, addon, annual), [seats, addon, annual]);
  const sliderFill = `${(seats / 300) * 100}%`;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "TimeTango — Employee Attendance & Workforce Management Software",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, Android, iOS, Windows, macOS",
    offers: {
      "@type": "Offer",
      url: "https://timetango.in/pricing",
      priceCurrency: "INR",
      price: "1500",
      priceValidUntil: "2027-12-31",
      availability: "https://schema.org/InStock",
    },
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="tt-page">
      <Helmet>
        <title>Pricing — TimeTango | Starting at ₹1,500/month</title>
        <meta name="description" content="Simple, transparent pricing for TimeTango attendance management. Graduated per-seat slabs from ₹50 to ₹30/user/month. Free trial, no card required. Activity tracking add-on available." />
        <meta property="og:title" content="Pricing — TimeTango | Starting at ₹1,500/month" />
        <meta property="og:description" content="Graduated per-seat pricing from ₹50 to ₹30/user/month. Attendance, GPS tracking, HRMS, leave management — desktop, web and mobile." />
        <meta property="og:image" content="https://timetango.in/assets/logo3.svg" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalURL} />
        <link rel="canonical" href={canonicalURL} />
        <meta property="og:site_name" content="TimeTango" />
        <meta name="language" content="english" />
        <meta name="robots" content="index, follow" />
        <meta name="google-site-verification" content="HeqJ8arMIZIDpCmRH1zlrNu601VS7Wp92JoTy_9bARM" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>

      {/* ── HEADER ── */}
      <section className="tt-hero" style={{paddingBottom:40}}>
        <div className="tt-wrap" style={{maxWidth:820}}>
          <div className="tt-eyebrow-pill">Transparent pricing · Free trial, no card</div>
          <h1 style={{fontSize:52,marginBottom:16}}>
            Simple, transparent pricing — <span className="tt-grad-text">starting at ₹1,500/month</span>
          </h1>
          <p className="tt-hero__sub">
            All-in-one attendance, GPS tracking, HRMS, leave management — desktop, web and mobile. Pick your team size; pay only for active employees.
          </p>
          {/* Billing toggle */}
          <div className="tt-toggle" style={{marginBottom:8}}>
            <button className={`tt-toggle__opt ${billing === 'monthly' ? 'tt-toggle__opt--active' : ''}`} onClick={() => setBilling('monthly')}>Monthly</button>
            <button className={`tt-toggle__opt ${billing === 'annual' ? 'tt-toggle__opt--active' : ''}`} onClick={() => setBilling('annual')}>Annual — save ~17%</button>
          </div>
        </div>
      </section>

      {/* ── TIER CARDS ── */}
      <section className="tt-section" style={{paddingTop:0}}>
        <div className="tt-wrap--grid">
          <div className="tt-tiers">
            {/* Small */}
            <div className="tt-tier">
              <div className="tt-tier__name">Small</div>
              <div className="tt-tier__range">1–50 users</div>
              <div className="tt-tier__price">₹50</div>
              <div className="tt-tier__price-sub">/ user / month</div>
              <div className="tt-tier__note">Minimum ₹1,500/month (covers up to 30 users)</div>
              <ul>
                <li>GPS & mobile attendance</li>
                <li>Leave management</li>
                <li>Self-service portal</li>
                <li>Guided onboarding</li>
              </ul>
              <Link to="/company-register" className="tt-btn tt-btn--grad tt-btn--pill" style={{width:'100%',justifyContent:'center'}}>Start free trial</Link>
            </div>

            {/* Medium */}
            <div className="tt-tier tt-tier--featured">
              <div className="tt-tier__badge">Most popular</div>
              <div className="tt-tier__name">Medium</div>
              <div className="tt-tier__range">51–100 users</div>
              <div className="tt-tier__price tt-grad-text">₹40</div>
              <div className="tt-tier__price-sub">/ user / month · for seats 51–100</div>
              <ul>
                <li>Everything in Small</li>
                <li>Geo-fencing</li>
                <li>Biometric & facial recognition</li>
                <li>Productivity analytics</li>
                <li>Payroll integration</li>
                <li>Priority support</li>
              </ul>
              <Link to="/company-register" className="tt-btn tt-btn--grad tt-btn--pill" style={{width:'100%',justifyContent:'center'}}>Start free trial</Link>
            </div>

            {/* Enterprise */}
            <div className="tt-tier">
              <div className="tt-tier__name">Enterprise</div>
              <div className="tt-tier__range">101+ users</div>
              <div className="tt-tier__price">₹30</div>
              <div className="tt-tier__price-sub">/ user / month · for seats above 100</div>
              <div className="tt-tier__note">Or a custom volume quote</div>
              <ul>
                <li>Everything in Medium</li>
                <li>SSO & advanced security</li>
                <li>Multi-location management</li>
                <li>Dedicated account manager & SLA</li>
              </ul>
              <a href="https://calendly.com/amol-amlapure" target="_blank" rel="noreferrer" className="tt-btn tt-btn--outline tt-btn--pill" style={{width:'100%',justifyContent:'center'}}>Talk to sales</a>
            </div>
          </div>
          <p style={{textAlign:'center',fontSize:13,color:'var(--tt-muted)',marginTop:20}}>
            Graduated slabs: first 50 seats at ₹50, next 50 at ₹40, 101+ at ₹30. All prices exclusive of 18% GST.
          </p>
        </div>
      </section>

      {/* ── COST CALCULATOR ── */}
      <section className="tt-section tt-section--tinted">
        <div className="tt-wrap--calc">
          <h2 style={{textAlign:'center',fontSize:32,marginBottom:32}}>Calculate your cost</h2>
          <div className="tt-calc">
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:4}}>
                <span style={{fontSize:15,fontWeight:600,color:'var(--tt-ink)'}}>Team size</span>
                <span style={{fontSize:28,fontWeight:700,fontFamily:"'Space Grotesk',sans-serif",color:'var(--tt-ink)'}}>{seats}</span>
              </div>
              <input
                type="range"
                min={1}
                max={300}
                value={seats}
                onChange={e => setSeats(+e.target.value)}
                style={{'--fill': sliderFill}}
              />
              <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'var(--tt-muted)'}}>
                <span>1</span><span>50</span><span>100</span><span>200</span><span>300</span>
              </div>
              <label style={{display:'flex',alignItems:'center',gap:10,marginTop:24,cursor:'pointer',fontSize:14,color:'var(--tt-body)'}}>
                <input type="checkbox" checked={addon} onChange={e => setAddon(e.target.checked)} style={{width:18,height:18,accentColor:'var(--tt-magenta)'}} />
                Add Activity Tracking — ₹100 / license
              </label>
            </div>
            <div className="tt-calc__result">
              <div style={{fontSize:13,color:'var(--tt-muted)',marginBottom:4}}>Estimated monthly cost</div>
              <div className="tt-calc__total tt-grad-text">₹{fmtINR(price.perMonth)}</div>
              <div style={{fontSize:13,color:'var(--tt-muted)'}}>
                / month{annual ? ', billed yearly' : ''} <span style={{color:'var(--tt-muted2)'}}>+ 18% GST</span>
              </div>
              <ul className="tt-calc__breakdown">
                <li><span>Seats subtotal ({seats} users)</span><span>₹{fmtINR(price.base)}</span></li>
                {price.floorApplied && <li style={{fontStyle:'italic',color:'var(--tt-muted)'}}><span>₹1,500 minimum applied</span><span>₹1,500</span></li>}
                {addon && <li><span>Activity Tracking add-on</span><span>₹{fmtINR(price.addonCost)}</span></li>}
                {annual && <li style={{color:'var(--tt-green)'}}><span>Annual discount (~17%)</span><span>−₹{fmtINR(price.discount)}</span></li>}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── ADD-ON STRIP ── */}
      <section className="tt-section">
        <div className="tt-wrap--grid">
          <div className="tt-addon">
            <div>
              <h4>Add Activity Tracking</h4>
              <p>Desktop agent captures active apps, idle time and productivity breakdowns.</p>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:16}}>
              <span style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:24,fontWeight:700,color:'var(--tt-ink2)'}}>₹100</span>
              <span style={{fontSize:13,color:'var(--tt-muted)'}}>per license / month</span>
              <Link to="/company-register" className="tt-btn tt-btn--outline tt-btn--pill" style={{padding:'8px 20px',fontSize:13}}>Add to plan</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ── */}
      <section className="tt-section tt-section--tinted" style={{textAlign:'center'}}>
        <div className="tt-wrap--grid">
          <span className="tt-eyebrow">Everything you need</span>
          <h2 style={{fontSize:32,marginBottom:32}}>What's included</h2>
          <div className="tt-included">
            {includedItems.map(item => (
              <div key={item} className="tt-included__item">
                <span className="tt-included__check">✓</span>
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPARISON TABLE ── */}
      <section className="tt-section">
        <div className="tt-wrap--grid">
          <h2 style={{textAlign:'center',fontSize:32,marginBottom:32}}>Compare plans</h2>
          <div style={{overflowX:'auto'}}>
            <table className="tt-compare">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Small</th>
                  <th className="tt-compare--featured">Medium</th>
                  <th>Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {compRows.map(r => (
                  <tr key={r.feat}>
                    <td>{r.feat}</td>
                    <td>{r.s ? <span className="check">✓</span> : <span className="dash">—</span>}</td>
                    <td>{r.m ? <span className="check">✓</span> : <span className="dash">—</span>}</td>
                    <td>{r.e ? <span className="check">✓</span> : <span className="dash">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="tt-section tt-section--tinted">
        <div className="tt-wrap--calc">
          <h2 style={{textAlign:'center',fontSize:32,marginBottom:32}}>Frequently asked questions</h2>
          {faqItems.map((f, i) => (
            <div key={i} className={`tt-faq-item ${openFaq === i ? 'tt-faq-item--open' : ''}`}>
              <button className="tt-faq-q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                {f.q}
                <span style={{fontSize:20,fontWeight:300,color:'var(--tt-muted)',transition:'transform 0.2s',transform:openFaq===i?'rotate(45deg)':'none'}}>+</span>
              </button>
              {openFaq === i && <div className="tt-faq-a">{f.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="tt-section">
        <div className="tt-wrap--cta">
          <div className="tt-cta-band">
            <h2>Start tracking attendance in minutes</h2>
            <p>Register your company, invite employees and go live — no setup fee, no lock-in.</p>
            <div style={{display:'flex',gap:14,justifyContent:'center',flexWrap:'wrap'}}>
              <Link to="/company-register" className="tt-btn tt-btn--white tt-btn--pill tt-btn--lg">Start free trial →</Link>
              <a href="https://calendly.com/amol-amlapure" target="_blank" rel="noreferrer" className="tt-btn tt-btn--pill tt-btn--lg" style={{background:'rgba(255,255,255,0.15)',color:'#fff',border:'1px solid rgba(255,255,255,0.3)'}}>Talk to sales</a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINE PRINT ── */}
      <section style={{padding:'24px 0 48px'}}>
        <div className="tt-wrap">
          <p className="tt-fine-print">
            All prices are in INR and exclusive of 18% GST. The minimum monthly charge is ₹1,500 (covers up to 30 users at ₹50/user). Graduated slab rates: 1–50 users at ₹50, 51–100 at ₹40, 101+ at ₹30. Billing is based on active employees. Annual billing is charged upfront with approximately 17% savings.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PricingRedesign;
